import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { agentById } from "@/lib/agents";
import { API_BASE, api, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Send, Cpu, Target, Users as UsersIcon, Loader2, ShieldCheck, X,
    ChevronDown, ChevronUp, Link2, Maximize2, Minimize2, Activity,
    History, SquarePen, MessagesSquare,
} from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import RobotAvatar from "./RobotAvatar";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * Conversation UI of the floating Master Chat widget.
 * Streams SSE from /agents/chat/stream; supports master switching, resource
 * context ("lead:<id>" / "candidate:<id>") and pre-filled prompts (both
 * arrive via the `incoming` prop, dispatched through openChat()).
 */
export default function ChatPanel({ expanded, onToggleExpand, onClose, incoming, onAgentDone }) {
    const { t } = useTranslation();
    const { refreshWorkspace } = useAuth();
    const [masterId, setMasterId] = useState("master_sales_agent");
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]); // {role, content, agentId?, streaming?}
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [events, setEvents] = useState([]);     // live agent-activity feed
    const [actOpen, setActOpen] = useState(false); // compact activity strip expanded?
    const [ctx, setCtx] = useState(null);          // resource context payload
    const [ctxLoading, setCtxLoading] = useState(false);
    const [ctxOpen, setCtxOpen] = useState(false);
    const [view, setView] = useState("chat");      // "chat" | "history"
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const scrollerRef = useRef(null);
    const abortRef = useRef(null);

    const master = agentById(masterId);
    const suggestions = masterId === "master_sales_agent"
        ? [t("chat.sug_sales_1"), t("chat.sug_sales_2"), t("chat.sug_sales_3")]
        : [t("chat.sug_rec_1"), t("chat.sug_rec_2"), t("chat.sug_rec_3")];

    // Auto-scroll the messages container (not the page) on new content.
    useEffect(() => {
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, events]);

    // Handle openChat() payloads: switch master, prefill prompt, load context.
    const lastIncomingTs = useRef(null);
    useEffect(() => {
        if (!incoming || incoming.ts === lastIncomingTs.current) return;
        lastIncomingTs.current = incoming.ts;
        if (incoming.masterId && incoming.masterId !== masterId) switchMaster(incoming.masterId);
        if (incoming.prompt) setInput(incoming.prompt);
        if (incoming.context) loadContext(incoming.context);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [incoming]);

    const switchMaster = (id) => {
        if (id === masterId) return;
        abortRef.current?.abort(); // stop an in-flight stream writing into the reset list
        setMasterId(id); setSessionId(null); setMessages([]); setEvents([]);
        setView("chat");
    };

    const openHistory = async () => {
        setView("history");
        setSessionsLoading(true);
        try {
            const { data } = await api.get("/agents/chat/sessions");
            setSessions(data);
        } catch (e) {
            console.warn("Sessions load failed", e?.message);
        } finally {
            setSessionsLoading(false);
        }
    };

    const openSession = async (s) => {
        try {
            const { data } = await api.get(`/agents/chat/history?session_id=${encodeURIComponent(s.session_id)}`);
            abortRef.current?.abort();
            setMasterId(s.master_id || "master_sales_agent");
            setSessionId(s.session_id);
            setMessages(data.map((d) => ({ role: d.role, content: d.content, agentId: d.agent_id })));
            setEvents([]); setCtx(null); setView("chat");
        } catch (e) {
            console.warn("History load failed", e?.message);
        }
    };

    const newChat = () => {
        abortRef.current?.abort();
        setSessionId(null); setMessages([]); setEvents([]); setCtx(null); setView("chat");
    };

    const loadContext = async (resource) => {
        setCtxLoading(true);
        try {
            const { data } = await api.get(`/chat/context?resource=${encodeURIComponent(resource)}`);
            setCtx(data);
            setCtxOpen(false);
        } catch (e) {
            console.warn("Context load failed", e?.response?.data?.detail || e?.message);
        } finally {
            setCtxLoading(false);
        }
    };

    const sendMessage = async (text) => {
        if (!text.trim() || busy) return;
        setBusy(true);
        setEvents([]);
        setInput("");

        // Stable id — safer than an index from a stale closure.
        const msgId = `m${Date.now()}`;
        setMessages((m) => [
            ...m,
            { role: "user", content: text },
            { id: msgId, role: "agent", agentId: masterId, content: "", streaming: true },
        ]);

        const ctrl = new AbortController();
        abortRef.current = ctrl;
        try {
            const res = await fetch(`${API_BASE}/agents/chat/stream`, {
                method: "POST",
                signal: ctrl.signal,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                    Accept: "text/event-stream",
                },
                body: JSON.stringify({
                    master_id: masterId,
                    session_id: sessionId,
                    message: text,
                    context: ctx?.system_context || null,
                    context_resource: ctx ? `${ctx.kind}:${ctx.resource_id}` : null,
                }),
            });
            if (!res.body) throw new Error("No stream body");

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const parts = buffer.split("\n\n");
                buffer = parts.pop() || "";
                for (const part of parts) {
                    const lines = part.split("\n");
                    let evType = "message";
                    let dataStr = "";
                    for (const ln of lines) {
                        if (ln.startsWith("event:")) evType = ln.slice(6).trim();
                        else if (ln.startsWith("data:")) dataStr += ln.slice(5).trim();
                    }
                    if (!dataStr) continue;
                    let data;
                    try { data = JSON.parse(dataStr); } catch { continue; }
                    handleEvent(evType, data, msgId);
                }
            }
        } catch (e) {
            if (e.name !== "AbortError") {
                setMessages((m) => m.map((mm) => mm.id === msgId ? { ...mm, content: mm.content + `\n\n[Error streaming: ${e.message}]`, streaming: false } : mm));
            }
        } finally {
            setBusy(false);
            refreshWorkspace();
        }
    };

    const handleEvent = (type, data, msgId) => {
        if (type === "session") {
            setSessionId(data.session_id);
        } else if (type === "token") {
            setMessages((m) => m.map((mm) => mm.id === msgId ? { ...mm, content: (mm.content || "") + (data.delta || "") } : mm));
        } else if (type === "done") {
            setMessages((m) => m.map((mm) => mm.id === msgId ? { ...mm, streaming: false } : mm));
            setEvents((ev) => [...ev, { type: "done", label: t("chat.ev_done", { n: data.credits_spent || 0 }), ts: Date.now() }]);
            onAgentDone?.();
        } else if (type === "master_started") {
            setEvents((ev) => [...ev, { type, label: t("chat.ev_started", { name: data.name }), ts: Date.now() }]);
        } else if (type === "plan") {
            setEvents((ev) => [...ev, { type, label: `${t("chat.ev_plan")}: ${data.sub_agents.join(" → ")}`, ts: Date.now() }]);
        } else if (type === "subagent_started") {
            setEvents((ev) => [...ev, { type, label: t("chat.ev_sub_started", { name: data.name, tools: (data.tools || []).join(", ") }), ts: Date.now() }]);
        } else if (type === "subagent_finished") {
            setEvents((ev) => [...ev, { type, label: `✓ ${data.name}: ${data.summary}`, ts: Date.now() }]);
        } else if (type === "master_thinking") {
            setEvents((ev) => [...ev, { type, label: t("chat.ev_thinking"), ts: Date.now() }]);
        }
    };

    const clearContext = () => setCtx(null);

    return (
        <>
            {/* Header */}
            <div className="h-14 shrink-0 px-3 sm:px-4 border-b border-white/10 flex items-center gap-2.5 bg-[var(--jr-surface)]/60">
                <div className="w-9 h-9 rounded-full grid place-items-center bg-[var(--jr-brand)]/12 border border-[var(--jr-brand)]/30 shrink-0">
                    <RobotAvatar size={26}/>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-white truncate leading-tight" data-testid="chat-master-name">{master?.name}</div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[var(--jr-text-3)] font-mono whitespace-nowrap truncate">Master Agent</div>
                </div>
                <button
                    onClick={() => switchMaster("master_sales_agent")}
                    data-testid="switch-sales" title="Sales"
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs transition-colors ${masterId === "master_sales_agent" ? "bg-[var(--jr-brand)] text-white" : "border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}
                >
                    <Target size={12}/>{expanded && <span className="hidden sm:inline">Sales</span>}
                </button>
                <button
                    onClick={() => switchMaster("master_recruiter_agent")}
                    data-testid="switch-recruiter" title="Recruiter"
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs transition-colors ${masterId === "master_recruiter_agent" ? "bg-[var(--jr-amber)] text-black" : "border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}
                >
                    <UsersIcon size={12}/>{expanded && <span className="hidden sm:inline">Recruiter</span>}
                </button>
                <button onClick={view === "history" ? () => setView("chat") : openHistory} data-testid="chat-history-btn"
                    title={t("chat.history_btn")}
                    className={`p-2 rounded-lg transition-colors ${view === "history" ? "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)]" : "hover:bg-white/8 text-[var(--jr-text-2)] hover:text-white"}`}>
                    <History size={14}/>
                </button>
                <button onClick={newChat} data-testid="chat-new-btn" title={t("chat.new_chat")}
                    className="p-2 rounded-lg hover:bg-white/8 text-[var(--jr-text-2)] hover:text-white transition-colors">
                    <SquarePen size={14}/>
                </button>
                <span className="w-px h-5 bg-white/10 mx-0.5"/>
                <button onClick={onToggleExpand} data-testid="chat-expand-btn"
                    title={expanded ? t("chat.shrink") : t("chat.expand")}
                    className="p-2 rounded-lg hover:bg-white/8 text-[var(--jr-text-2)] hover:text-white transition-colors">
                    {expanded ? <Minimize2 size={14}/> : <Maximize2 size={14}/>}
                </button>
                <button onClick={onClose} data-testid="chat-close-btn" title={t("chat.bubble_close")}
                    className="p-2 rounded-lg hover:bg-white/8 text-[var(--jr-text-2)] hover:text-white transition-colors">
                    <X size={14}/>
                </button>
            </div>

            {/* Body */}
            <div className={`flex-1 min-h-0 ${expanded ? "grid lg:grid-cols-[1fr_300px]" : "flex flex-col"}`}>
                <div className="flex flex-col min-h-0 overflow-hidden">
                    {view === "history" ? (
                        <HistoryList sessions={sessions} loading={sessionsLoading}
                            onOpen={openSession} onNew={newChat} expanded={expanded}/>
                    ) : (<>
                    <div ref={scrollerRef} className={`flex-1 overflow-y-auto ${expanded ? "px-8 py-6" : "px-4 py-4"} space-y-5`} data-testid="chat-messages">
                        {messages.length === 0 && (
                            <div className="text-center py-6">
                                <div className="mx-auto w-16 h-16 rounded-2xl grid place-items-center bg-[var(--jr-brand)]/10 border border-[var(--jr-brand)]/25">
                                    <RobotAvatar size={44}/>
                                </div>
                                <h2 className="font-display text-lg mt-3 tracking-tight">{t("chat.start_title")}</h2>
                                <p className="text-sm text-[var(--jr-text-2)] mt-1">{t("chat.start_sub")}</p>
                                <div className={`mt-4 grid gap-2 mx-auto ${expanded ? "sm:grid-cols-3 max-w-3xl" : "grid-cols-1"}`}>
                                    {suggestions.map((s, i) => (
                                        <button key={i} onClick={() => sendMessage(s)} data-testid={`suggestion-${i}`}
                                            className="text-left rounded-xl border border-white/10 bg-[var(--jr-surface)] p-3 text-sm text-[var(--jr-text-2)] hover:border-[var(--jr-brand)]/40 hover:text-white transition-colors">
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((m, i) => <Message key={i} msg={m} />)}
                    </div>

                    {/* Compact-mode: slim collapsible agent-activity strip */}
                    {!expanded && events.length > 0 && (
                        <ActivityStrip events={events} open={actOpen} onToggle={() => setActOpen(!actOpen)} />
                    )}

                    {/* Composer */}
                    <div className={`border-t border-white/8 shrink-0 ${expanded ? "px-8 py-5" : "px-3 py-3"}`}>
                        {(ctxLoading || ctx) && (
                            <ContextPanel ctx={ctx} loading={ctxLoading} open={ctxOpen} onToggle={() => setCtxOpen(!ctxOpen)} onClear={clearContext}/>
                        )}
                        <form
                            onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
                            className="flex items-end gap-2 rounded-xl border border-white/10 bg-[var(--jr-surface)] p-2.5 focus-within:border-[var(--jr-brand)]/50 transition-colors"
                        >
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        sendMessage(input);
                                    }
                                }}
                                placeholder={t("chat.input_placeholder", { name: master?.name })}
                                rows={1}
                                data-testid="chat-input"
                                className="flex-1 bg-transparent border-0 outline-none resize-none text-white placeholder:text-[var(--jr-text-3)] text-sm min-h-[28px] max-h-[120px]"
                            />
                            <button
                                type="submit"
                                disabled={busy || !input.trim()}
                                data-testid="chat-send"
                                className="w-9 h-9 rounded-lg bg-[var(--jr-brand)] text-white grid place-items-center hover:bg-[var(--jr-brand-2)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                            >
                                {busy ? <Loader2 className="animate-spin" size={15}/> : <Send size={15}/>}
                            </button>
                        </form>
                        {expanded && (
                            <div className="mt-2 hidden sm:flex items-center justify-between text-[10px] text-[var(--jr-text-3)] font-mono uppercase tracking-[0.16em]">
                                <span>{t("chat.shift_enter")}</span>
                                <span className="inline-flex items-center gap-1"><ShieldCheck size={10}/> {t("chat.approval_mode")}</span>
                            </div>
                        )}
                    </div>
                    </>)}
                </div>

                {/* Expanded-mode: live agent thinking sidebar */}
                {expanded && (
                    <aside className="border-l border-white/8 bg-[var(--jr-bg)] overflow-y-auto hidden lg:block" data-testid="thinking-panel">
                        <div className="px-5 py-4 border-b border-white/8 sticky top-0 bg-[var(--jr-bg)] backdrop-blur z-10">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--jr-text-3)] font-mono">{t("chat.live_thinking")}</div>
                            <h3 className="font-display text-lg tracking-tight mt-1">{t("chat.agent_stack")}</h3>
                        </div>
                        <div className="p-4 space-y-2">
                            {events.length === 0 ? (
                                <div className="text-xs text-[var(--jr-text-3)]">{t("chat.events_empty")}</div>
                            ) : (
                                events.map((ev, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs jr-fadein">
                                        <span className="font-mono text-[10px] text-[var(--jr-text-3)] mt-0.5 min-w-[42px]">{new Date(ev.ts).toLocaleTimeString().slice(0, 5)}</span>
                                        <div className={`flex-1 ${ev.type === "subagent_finished" ? "text-[var(--jr-text-2)]" : "text-white"}`}>{ev.label}</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </aside>
                )}
            </div>
        </>
    );
}

function ActivityStrip({ events, open, onToggle }) {
    const { t } = useTranslation();
    const last = events[events.length - 1];
    return (
        <div className="border-t border-white/8 bg-[var(--jr-surface)]/50 shrink-0">
            <button type="button" onClick={onToggle} data-testid="chat-activity-toggle"
                className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-white/[0.03] transition-colors">
                <Activity size={11} className="text-[var(--jr-brand)] shrink-0"/>
                <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] shrink-0">{t("chat.activity")}</span>
                <span className="flex-1 text-[11px] text-[var(--jr-text-2)] truncate">{last?.label}</span>
                {open ? <ChevronDown size={12} className="text-[var(--jr-text-3)] shrink-0"/> : <ChevronUp size={12} className="text-[var(--jr-text-3)] shrink-0"/>}
            </button>
            {open && (
                <div className="max-h-36 overflow-y-auto px-4 pb-2 space-y-1" data-testid="chat-activity-list">
                    {events.map((ev, i) => (
                        <div key={i} className="flex items-start gap-2 text-[11px]">
                            <span className="font-mono text-[9px] text-[var(--jr-text-3)] mt-0.5 min-w-[38px]">{new Date(ev.ts).toLocaleTimeString().slice(0, 5)}</span>
                            <span className={`flex-1 ${ev.type === "subagent_finished" ? "text-[var(--jr-text-2)]" : "text-white"}`}>{ev.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Message({ msg }) {
    const { t } = useTranslation();
    const agent = agentById(msg.agentId);
    if (msg.role === "user") {
        return (
            <div className="flex justify-end" data-testid="bubble-user">
                <div className="max-w-[85%] rounded-2xl bg-[var(--jr-surface-2)] border border-white/10 px-4 py-2.5 text-sm text-white whitespace-pre-wrap">
                    {msg.content}
                </div>
            </div>
        );
    }
    return (
        <div className="flex gap-2.5" data-testid="bubble-agent">
            <div className="w-8 h-8 rounded-md bg-[var(--jr-brand)]/15 grid place-items-center shrink-0">
                <Cpu size={14} className="text-[var(--jr-brand)]"/>
            </div>
            <div className="flex-1 min-w-0 border-l-2 border-[var(--jr-brand)] pl-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)] mb-1">
                    {agent?.name || msg.agentId}
                </div>
                <div className="text-sm text-white leading-relaxed break-words jr-md">
                    {msg.content
                        ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        : (msg.streaming ? <span className="text-[var(--jr-text-2)]">{t("chat.thinking")}</span> : "")}
                    {msg.streaming && msg.content && <span className="jr-caret ml-0.5">▋</span>}
                </div>
            </div>
        </div>
    );
}

function ContextPanel({ ctx, loading, open, onToggle, onClear }) {
    const { t } = useTranslation();
    if (loading && !ctx) {
        return (
            <div data-testid="chat-context-loading"
                className="mb-3 flex items-center gap-2 text-xs text-[var(--jr-text-2)] px-3 py-2 rounded-lg border border-white/8 bg-[var(--jr-surface)]">
                <Loader2 className="animate-spin" size={12}/>
                <span>{t("chat.ctx_loading")}</span>
            </div>
        );
    }
    if (!ctx) return null;
    const sectionsCount = (ctx.sections || []).length;
    return (
        <div data-testid="chat-context-pill"
            className="mb-3 rounded-lg border border-[var(--jr-brand)]/25 bg-[var(--jr-brand)]/6">
            <div className="w-full flex items-center gap-1 px-3 py-2 rounded-lg hover:bg-[var(--jr-brand)]/10 transition-colors">
                <button type="button" onClick={onToggle}
                    data-testid="chat-context-toggle"
                    className="flex-1 flex items-center gap-2 text-left min-w-0">
                    <Link2 size={13} className="text-[var(--jr-brand)] shrink-0"/>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)]">
                            {t("chat.ctx_title")} · {ctx.kind}
                        </div>
                        <div className="text-xs text-white truncate mt-0.5" data-testid="chat-context-label">{ctx.label}</div>
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--jr-text-3)] hidden sm:inline">
                        {sectionsCount} {t("chat.ctx_sections")}
                    </span>
                    {open ? <ChevronUp size={13} className="text-[var(--jr-text-3)]"/> : <ChevronDown size={13} className="text-[var(--jr-text-3)]"/>}
                </button>
                <button type="button" onClick={onClear}
                    data-testid="chat-context-clear" title={t("chat.ctx_clear")}
                    className="ml-1 p-1 rounded hover:bg-white/10 text-[var(--jr-text-3)] hover:text-white shrink-0">
                    <X size={11}/>
                </button>
            </div>
            {open && (
                <div className="border-t border-[var(--jr-brand)]/15 px-3 py-3 space-y-3 max-h-64 overflow-y-auto">
                    {(ctx.sections || []).map((s, i) => (
                        <div key={i} data-testid={`chat-context-section-${i}`}>
                            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)] mb-1">{s.title}</div>
                            <ul className="space-y-0.5 text-xs text-[var(--jr-text-2)] leading-relaxed">
                                {(s.lines || []).map((ln, j) => (
                                    <li key={j} className="flex items-start gap-1.5">
                                        <span className="text-[var(--jr-text-3)] mt-1">·</span>
                                        <span className="flex-1 break-words">{ln}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function HistoryList({ sessions, loading, onOpen, onNew, expanded }) {
    const { t } = useTranslation();
    return (
        <div className={`flex-1 overflow-y-auto ${expanded ? "px-8 py-6" : "px-4 py-4"}`} data-testid="chat-history-list">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-base tracking-tight">{t("chat.history_title")}</h3>
                <button type="button" onClick={onNew} data-testid="chat-history-new"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[var(--jr-brand)] text-white hover:bg-[var(--jr-brand-2)] transition-colors">
                    <SquarePen size={11}/> {t("chat.new_chat")}
                </button>
            </div>
            {loading ? (
                <div className="flex items-center gap-2 text-xs text-[var(--jr-text-2)] py-6 justify-center">
                    <Loader2 className="animate-spin" size={13}/> {t("common.loading")}
                </div>
            ) : sessions.length === 0 ? (
                <div className="text-center py-8">
                    <MessagesSquare size={22} className="mx-auto text-[var(--jr-text-3)]"/>
                    <p className="text-sm text-[var(--jr-text-2)] mt-2">{t("chat.history_empty")}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {sessions.map((s, i) => {
                        const m = agentById(s.master_id);
                        const isRecruiter = s.master_id === "master_recruiter_agent";
                        return (
                            <button key={s.session_id} type="button" onClick={() => onOpen(s)}
                                data-testid={`chat-session-${i}`}
                                className="w-full text-left rounded-xl border border-white/8 bg-[var(--jr-surface)] p-3 hover:border-[var(--jr-brand)]/40 transition-colors">
                                <div className="flex items-center gap-2 text-[9px] font-mono uppercase tracking-[0.16em]">
                                    <span style={{ color: isRecruiter ? "var(--jr-amber)" : "var(--jr-brand)" }}>{m?.name || s.master_id}</span>
                                    <span className="text-[var(--jr-text-3)]">{fmtDateTime(s.last_at)}</span>
                                    <span className="ml-auto text-[var(--jr-text-3)]">{s.count} {t("chat.msgs")}</span>
                                </div>
                                <div className="text-xs text-[var(--jr-text-2)] mt-1.5 line-clamp-2 leading-relaxed">{s.last_message}</div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
